<template>
  <div class="punch_clock">
    <clock-header ref="punch_clock_header" />

    <div class="vx-card p-6">
      <vs-table id="table-loading" class="vs-con-loading__container" stripe :sst="true" :data="punchClocks">
        <template slot="header">
          <div class="mb-4 md:mb-0 mr-4 ag-grid-table-actions-left w-full">
            <vs-row vs-justify="flex-end">
              <vx-tooltip text="Excel Report">
                <vs-button class="mr-1 my-5" type="filled" color="dark" @click="downloadExcel">Excel</vs-button>
              </vx-tooltip>
            </vs-row>
          </div>
        </template>

        <template slot="thead">
          <vs-th>Sr#</vs-th>
          <vs-th>Employee</vs-th>
          <vs-th>Username</vs-th>
          <vs-th>Date</vs-th>
          <vs-th>In</vs-th>
          <vs-th>Out</vs-th>
          <vs-th>Total (hrs + min)</vs-th>
          <vs-th>Action</vs-th>
        </template>

        <template slot-scope="{ data }" ref="tableBody">
          <vs-tr :data="tr" :key="i" v-for="(tr, i) in data">
            <vs-td>
              {{ page * length - (length - i - 1) }}
            </vs-td>
            <vs-td>
              {{ tr.employer && `${tr.employer.firstname} ${tr.employer.lastname}` }}
            </vs-td>
            <vs-td>
              {{ tr.employer && tr.employer.username }}
            </vs-td>
            <vs-td>
              {{ formatDate(tr.created_at) }}
            </vs-td>
            <vs-td>
              <flat-pickr v-if="tr.edit_mode" :value="tr.in_time" class="w-full" :config="config" @input="(item) => SET_STATE({ action: `punchClocks[${i}].in_time`, data: item })" />
              <span v-else>
                {{ tr.in_time }}
              </span>
            </vs-td>
            <vs-td>
              <flat-pickr v-if="tr.edit_mode" :value="tr.out_time" class="w-full" :config="config" @input="(item) => SET_STATE({ action: `punchClocks[${i}].out_time`, data: item })" />
              <span v-else>
                {{ tr.out_time || '-' }}
              </span>
            </vs-td>
            <vs-td>
              {{ tr.out_time ? calculateTimeDiff(tr) : '-' }}
            </vs-td>
            <vs-td>
              <div class="inline-flex" v-if="!tr.edit_mode">
                <vx-tooltip text="Edit Punch Clock" v-if="checkPermissionByIds([permissions.PUNCH_CLOCK_EDIT])">
                  <feather-icon icon="Edit3Icon" svgClasses="h-5 w-5 mr-4 hover:text-primary cursor-pointer" @click="SET_STATE({ action: `punchClocks[${i}].edit_mode`, data: true })" />
                </vx-tooltip>
                <vx-tooltip text="Delete Punch Clock" v-if="checkPermissionByIds([permissions.PUNCH_CLOCK_DELETE])">
                  <feather-icon icon="Trash2Icon" svgClasses="h-5 w-5 hover:text-danger cursor-pointer" @click="deletePunchClock(tr.id)" />
                </vx-tooltip>
              </div>

              <div class="inline-flex" v-if="tr.edit_mode">
                <vx-tooltip text="Update Punch Clock">
                  <feather-icon icon="SaveIcon" svgClasses="h-5 w-5 mr-4 hover:text-primary cursor-pointer" @click="saveEditableData(i)" />
                </vx-tooltip>
                <vx-tooltip text="Cancel">
                  <feather-icon icon="XCircleIcon" svgClasses="h-5 w-5 mr-4 hover:text-danger cursor-pointer" @click="SET_STATE({ action: `punchClocks[${i}].edit_mode`, data: false })" />
                </vx-tooltip>
              </div>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </div>
  </div>
</template>

<script>
import XLSX from 'xlsx'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import ClockHeader from '@/views/punch-clock/components/ClockHeader.vue'
import { dateMixins } from '@/mixins/dateMixins.js'
import moment from 'moment'
import FlatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'

export default {
  name: 'PunchClockList',

  components: {
    ClockHeader,
    FlatPickr
  },

  data() {
    return {
      page: 1,
      length: 10,
      config: {
        enableTime: true,
        noCalendar: true,
        allowInput: true,
        time_24hr: false,
        formatDate: (d) => {
          return moment(d).format('HH:mm:ss')
        }
      }
    }
  },

  mixins: [dateMixins],

  computed: {
    ...mapState('punch_clock', { punchClocks: 'punchClocks' }),
    ...mapState('permission', ['permissions']),
    ...mapGetters('auth', ['checkPermissionByIds']),

    // calculate hours and times
    calculateTimeDiff() {
      return (data) => {
        const duration = moment.duration(moment(data.out_time, 'HH:mm:ss').diff(moment(data.in_time, 'HH:mm:ss')))
        const hours = parseInt(duration.asHours())
        const minutes = parseInt(duration.asMinutes()) % 60

        return `${hours} hours and ${minutes} minutes`
      }
    }
  },

  methods: {
    ...mapActions('punch_clock', { removePunchClock: 'deletePunchClock', updatePunchData: 'updatePunchClock' }),
    ...mapMutations('punch_clock', ['SET_STATE']),

    // delete punch clock - confirmation
    deletePunchClock(id) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirm Delete',
        text: 'Are you sure, you want to delete this record?',
        accept: () => this.deletePunchClockRecord(id),
        acceptText: 'Delete'
      })
    },

    // delete punch clock
    async deletePunchClockRecord(id) {
      try {
        const { message } = await this.removePunchClock(id)

        if (this.punchClocks.length === 1 && this.page !== 1) {
          this.page -= 1
        }

        this.$vs.notify({
          title: 'Success',
          text: message,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'success'
        })

        this.$nextTick(() => {
          this.$refs['punch_clock_header'].getData()
        })
      } catch ({ message }) {
        this.$vs.notify({
          title: 'Error',
          text: message,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger'
        })
      }
    },

    // update punch clock
    async saveEditableData(index) {
      try {
        const punchData = this.punchClocks[index]
        const { message } = await this.updatePunchData({ id: punchData.id, data: punchData })
        this.$vs.notify({
          title: 'Success',
          text: message,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'success'
        })

        this.$nextTick(() => {
          this.$refs['punch_clock_header'].getData()
        })
      } catch ({ message }) {
        this.$vs.notify({
          title: 'Error',
          text: message,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger'
        })
      }
    },

    // download excel
    downloadExcel() {
      const wb = XLSX.utils.book_new()

      // csv headers
      const columnHeader = ['#no', 'Employee', 'Username', 'Date', 'In Time', 'Out Time', 'Total (hrs + min)']

      const data = this.punchClocks.map((item, index) => ({
        '#no': index + 1,
        Employee: item.employer && `${item.employer.firstname} ${item.employer.lastname}`,
        Username: item.employer && item.employer.username,
        Date: dateMixins.methods.formatDate(item.created_at),
        'In Time': item.in_time,
        'Out Time': item.out_time || '-',
        'Total (hrs + min)': item.out_time ? this.calculateTimeDiff(item) : '-'
      }))

      const ws = XLSX.utils.aoa_to_sheet([columnHeader], { origin: 'A2' })

      XLSX.utils.sheet_add_aoa(ws, [['Punch Clock']], { origin: 'A1' })

      XLSX.utils.sheet_add_json(ws, data, { origin: 'A4', skipHeader: true })

      if (this.punchClocks.length === 0) {
        XLSX.utils.sheet_add_aoa(ws, [['No Record Found']], { origin: 'A4' })
      }

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1')
      XLSX.writeFile(wb, 'Punch Clock List.xlsx')
    }
  },
  beforeRouteLeave(_, __, next) {
    this.SET_STATE({
      action: 'punchClocks',
      data: []
    })

    next()
  }
}
</script>

<style>
</style>